import { USER_ROLE_LABELS } from '@wms/domain';
import { Component, useEffect, useRef, useState, type ErrorInfo, type ReactNode } from 'react';
import { api } from './api';
import { waitingAt } from './flow';
import { HelpButton } from './help';
import { environmentLabel, useAppInfo, useNewVersion } from './info';
import { FLOOR_TABS, SELLER_TABS, isActive, visibleTo, type NavGroup, type NavItem } from './nav';
import { navigate, useRoute, type Route } from './router';
import { SessionGate, useSession } from './session';
import { TabBar } from './tabbar';
import { useScreenWakeLock } from './wake';
import type { FloorSummary, SellerSummary } from './types';
import { Spinner, StaleScope, ToastProvider, useLoad } from './ui';
import { Icon } from './ui/icons';
import { Logo } from './ui/Logo';
import { Dashboard } from './views/Dashboard';
import { DeskPrinter } from './views/DeskPrinter';
import { Dispatch } from './views/Dispatch';
import { AdminUsers, SellerUsers } from './views/admin/Users';
import { Carriers } from './views/admin/Carriers';
import { ListPrice } from './views/admin/ListPrice';
import { Operations } from './views/admin/Operations';
import { WarehouseBuilder } from './views/admin/warehouse/Builder';
import { ImportProducts } from './views/catalog/Import';
import { Mapping } from './views/catalog/Mapping';
import { ProductForm } from './views/catalog/ProductForm';
import { Products } from './views/catalog/Products';
import { NewTenant, TenantPage, Tenants } from './views/admin/Tenants';
import { ChangePassword } from './views/ChangePassword';
import { Counts } from './views/Counts';
import { Exceptions } from './views/Exceptions';
import { FloorMap } from './views/FloorMap';
import { Login } from './views/Login';
import { ResetPassword } from './views/ResetPassword';
import { NOTIFICATIONS_CHANGED, Notifications } from './views/Notifications';
import { Orders } from './views/Orders';
import { Packing } from './views/Packing';
import { PickRoutes } from './views/PickRoutes';
import { Performance } from './views/Performance';
import { PickWalk } from './views/PickWalk';
import { Putaway } from './views/Putaway';
import { Receiving } from './views/Receiving';
import { ReturnsDesk } from './views/ReturnsDesk';
import { TwoFactorGate } from './views/Security';
import { Settings } from './views/Settings';
import { SellerAsn } from './views/seller/Asn';
import { SellerBilling } from './views/seller/Billing';
import { SellerDocuments } from './views/seller/Documents';
import { SellerHome } from './views/seller/Home';
import { SellerProducts } from './views/seller/Products';
import { SellerProfit } from './views/seller/Profit';
import { Marketplaces } from './views/seller/Marketplaces';
import { NewOrder } from './views/seller/NewOrder';
import { OrderImport } from './views/seller/OrderImport';
import { SellerReturns } from './views/seller/Returns';
import { Stock } from './views/Stock';
import { StockWaiting } from './views/StockWaiting';
import { UrgentBar, UrgentPanel, UrgentProvider } from './views/Urgent';

export function App() {
  const route = useRoute();
  // the link of a "Şifremi unuttum" e-mail opens without a session, whoever is signed in here; its token
  // is taken out of the address bar (and so out of the browser history) at once
  const linked = route.parts[0] === 'sifre-yenile' ? route.parts[1] : undefined;
  const [resetToken, setResetToken] = useState<string | null>(linked ?? null);
  useEffect(() => {
    if (!linked) {
      // the page reloaded after the token left the address: nothing to reset with
      if (route.parts[0] === 'sifre-yenile' && !resetToken) navigate('/');
      return;
    }
    setResetToken(linked);
    window.history.replaceState(null, '', `${window.location.pathname}#/sifre-yenile`);
  }, [linked, route.parts, resetToken]);
  if (resetToken) return <ResetPassword token={resetToken} />;
  return (
    <ToastProvider>
      <SessionGate loading={<Spinner />} login={(onToken) => <Login onToken={onToken} />}>
        <PasswordGate />
      </SessionGate>
    </ToastProvider>
  );
}

const FLOOR: readonly NavItem[] = [
  { path: '/', label: 'Özet', icon: 'home' },
  { path: '/kabul', label: 'Mal kabul', icon: 'inbox', waiting: (s) => waitingAt(s, '/kabul') },
  { path: '/iade', label: 'İade kabul', short: 'İade', icon: 'undo', count: (s) => s.expectedReturns ?? 0 },
  { path: '/yerlestirme', label: 'Yerleştirme', short: 'Yerleşt.', icon: 'shelf' },
  { path: '/toplama', label: 'Toplama', icon: 'route', waiting: (s) => waitingAt(s, '/toplama') },
  { path: '/paketleme', label: 'Paketleme', short: 'Paket', icon: 'box', waiting: (s) => waitingAt(s, '/paketleme') },
  { path: '/sevkiyat', label: 'Sevkiyat', icon: 'truck', waiting: (s) => waitingAt(s, '/sevkiyat') },
  { path: '/sorunlu', label: 'Sorunlu siparişler', short: 'Sorunlu', icon: 'alert', count: (s) => s.orders.on_hold ?? 0 },
  // in red like the problem orders: an order waiting for goods the warehouse may hold unentered
  { path: '/stok-bekleyen', label: 'Stok bekleyen siparişler', short: 'Stoksuz', icon: 'clock', count: (s) => s.orders.backordered ?? 0 },
  { path: '/sayim', label: 'Sayım', icon: 'clipboard', count: (s) => (s.counts?.open ?? 0) + (s.counts?.review ?? 0) },
];
const RECORDS: readonly NavItem[] = [
  { path: '/siparisler', label: 'Siparişler', icon: 'list' },
  { path: '/stok', label: 'Stok', icon: 'stock' },
  { path: '/urunler', label: 'Ürünler', icon: 'tag', roles: ['admin', 'supervisor'] },
  { path: '/harita', label: 'Depo haritası', short: 'Harita', icon: 'map' },
  { path: '/performans', label: 'Performans', icon: 'chart', roles: ['admin', 'supervisor'] },
];
const ADMIN: readonly NavItem[] = [
  { path: '/yonetim/musteriler', label: 'Müşteriler', icon: 'building', roles: ['admin', 'supervisor'] },
  { path: '/yonetim/kullanicilar', label: 'Kullanıcılar', icon: 'users', roles: ['admin'] },
  { path: '/yonetim/depo', label: 'Depo tanımı', short: 'Depo', icon: 'grid', roles: ['admin', 'supervisor'] },
  { path: '/yonetim/kargo', label: 'Kargo anlaşmaları', short: 'Kargo', icon: 'truck', roles: ['admin', 'supervisor'] },
  { path: '/yonetim/fiyatlar', label: 'Fiyat listesi', short: 'Fiyatlar', icon: 'receipt', roles: ['admin', 'supervisor'] },
  { path: '/yonetim/sistem', label: 'Sistem durumu', short: 'Sistem', icon: 'pulse', roles: ['admin', 'supervisor'], count: (s) => s.ops?.critical ?? 0 },
];

const SELLER: readonly NavItem[] = [
  { path: '/', label: 'Özet', icon: 'home' },
  { path: '/siparisler', label: 'Siparişler', icon: 'list' },
  { path: '/iadeler', label: 'İadeler', icon: 'undo' },
  { path: '/asn', label: 'Depoya gönderiler', short: 'Gönderiler', icon: 'inbox' },
  { path: '/urunler', label: 'Ürünler ve stok', short: 'Ürün/stok', icon: 'stock' },
  { path: '/e-belge', label: 'e-Belgeler', icon: 'doc', count: (s) => s.documentsPending ?? 0 },
  { path: '/pazaryerleri', label: 'Pazaryeri hesapları', short: 'Pazaryeri', icon: 'plug', count: (s) => s.marketplaces?.auth_failed ?? 0 },
  { path: '/performans', label: 'Performans', icon: 'chart' },
  { path: '/karlilik', label: 'Kârlılık', icon: 'trend' },
  { path: '/faturalar', label: 'Ücretler ve faturalar', short: 'Faturalar', icon: 'receipt' },
  { path: '/kullanicilar', label: 'Kullanıcılar', icon: 'users', roles: ['owner', 'manager'] },
];
const SELLER_SECTIONS = ['', 'siparisler', 'iadeler', 'asn', 'stok', 'urunler', 'e-belge', 'pazaryerleri', 'performans', 'karlilik', 'faturalar', 'kullanicilar', 'sifre', 'guvenlik', 'ayarlar', 'bildirimler'];

const TITLES: Record<string, string> = {
  '': 'Özet',
  kabul: 'Mal kabul',
  iade: 'İade kabul masası',
  iadeler: 'Müşteri iadeleri',
  yerlestirme: 'Yerleştirme ve rafa iade',
  toplama: 'Toplama',
  paketleme: 'Paketleme masası',
  sevkiyat: 'Sevkiyat',
  sorunlu: 'Sorunlu siparişler',
  'stok-bekleyen': 'Stok bekleyen siparişler',
  sayim: 'Göz sayımı',
  acil: 'Acil işler',
  siparisler: 'Siparişler',
  stok: 'Stok',
  harita: 'Depo haritası',
  asn: 'Depoya gönderiler',
  'e-belge': 'e-Belgeler',
  pazaryerleri: 'Pazaryeri hesapları',
  faturalar: 'Depo hizmet ücretleri ve faturalar',
  kullanicilar: 'Kullanıcılar',
  sifre: 'Şifremi değiştir',
  guvenlik: 'Ayarlar',
  ayarlar: 'Ayarlar',
  bildirimler: 'Bildirimler',
  performans: 'Performans',
  karlilik: 'Kârlılık',
  urunler: 'Ürünler',
  'urunler/yeni': 'Yeni ürün',
  'siparisler/yeni': 'Yeni manuel sipariş',
  'siparisler/ice-aktar': 'Excel / CSV ile sipariş yükleme',
  'urunler/ice-aktar': 'Excel / CSV ile ürün yükleme',
  'urunler/eslestirme': 'Pazaryeri eşleştirme',
  'yonetim/musteriler': 'Müşteriler',
  'yonetim/kullanicilar': 'Kullanıcılar',
  'yonetim/depo': 'Depo tanımı',
  'yonetim/kargo': 'Kargo anlaşmaları',
  'yonetim/fiyatlar': 'Fiyat listesi',
  'yonetim/sistem': 'Sistem durumu',
};
/** A seller's screens named apart from the warehouse's. */
const SELLER_TITLES: Record<string, string> = { urunler: 'Ürünler ve stok' };

/** The title where the top bar is a phone's: the words that fit beside the buttons. */
const SHORT_TITLES: Record<string, string> = {
  iade: 'İade kabul',
  iadeler: 'İadeler',
  yerlestirme: 'Yerleştirme',
  paketleme: 'Paketleme',
  sorunlu: 'Sorunlu',
  'stok-bekleyen': 'Stok bekleyen',
  sayim: 'Sayım',
  harita: 'Harita',
  asn: 'Gönderiler',
  pazaryerleri: 'Pazaryerleri',
  faturalar: 'Faturalar',
  sifre: 'Şifre',
  'siparisler/yeni': 'Yeni sipariş',
  'siparisler/ice-aktar': 'Sipariş yükle',
  'urunler/ice-aktar': 'Ürün yükle',
  'urunler/eslestirme': 'Eşleştirme',
  'yonetim/kargo': 'Kargo',
  'yonetim/fiyatlar': 'Fiyatlar',
  'yonetim/sistem': 'Sistem',
};

/** A temporary password is replaced, and a role that needs it sets up two-factor sign-in, before the app opens. */
function PasswordGate() {
  const session = useSession();
  if (session.me.mustChangePassword) return <ChangePassword forced />;
  if (session.me.twoFactor.required && !session.me.twoFactor.enabled) return <TwoFactorGate />;
  return <Shell />;
}

function Shell() {
  const session = useSession();
  const route = useRoute();
  const section = route.parts[0] ?? '';
  const operator = session.isOperator;
  // handhelds on the floor keep their screen on
  useScreenWakeLock(operator);

  // sellers have their portal, the floor screens are the warehouse's (a password reset link is App's own page)
  useEffect(() => {
    if (operator) return;
    // a seller's Stok is part of Ürünler ve stok now: old bookmarks and links land there with their filter
    if (section === 'stok') window.location.replace(`#/urunler${route.query.toString() ? `?${route.query}` : ''}`);
    else if (section !== 'sifre-yenile' && !SELLER_SECTIONS.includes(section)) navigate('/');
  }, [operator, section]);

  // badge counts: the floor's queues for staff, documents and broken accounts for sellers
  const summary = useLoad<(FloorSummary & SellerSummary) | undefined>(
    () =>
      (operator
        ? api<FloorSummary>('GET', `/api/wms/summary${session.warehouseId ? `?warehouseId=${session.warehouseId}` : ''}`)
        : api<SellerSummary>('GET', '/api/portal/summary')) as Promise<FloorSummary & SellerSummary>,
    [operator, session.warehouseId],
    30_000,
  );
  // another screen: the tabs' work counts as they are now (the work just done moved them)
  const counted = useRef(section);
  useEffect(() => {
    if (counted.current === section) return;
    counted.current = section;
    void summary.reload();
  }, [section]);

  const nav = (items: readonly NavItem[]) =>
    visibleTo(items, session.me.role).map((item) => {
      const active = isActive(item.path, route.path);
      const count = summary.data && item.count ? item.count(summary.data) : 0;
      return (
        <a key={item.path} href={`#${item.path}`} className={`nav-item${active ? ' active' : ''}`} title={item.label} aria-label={item.label}>
          <Icon name={item.icon} />
          <span className="label">{item.label}</span>
          <span className="short" aria-hidden>
            {item.short ?? item.label}
          </span>
          {count > 0 && <span className="count">{count}</span>}
        </a>
      );
    });

  const roleLabel = `${operator ? '' : 'Satıcı · '}${USER_ROLE_LABELS[session.me.role]}`;
  const titleKey = TITLES[route.parts.slice(0, 2).join('/')] ? route.parts.slice(0, 2).join('/') : section;
  const info = useAppInfo();
  const newVersion = useNewVersion();
  const ribbon = environmentLabel(info?.environment);
  const warehouse = session.warehouses.find((w) => w.id === session.warehouseId);
  const panelTitle = operator ? `Depo yönetimi · ${warehouse?.name ?? 'Depo'}` : `Müşteri paneli · ${session.me.tenant?.name ?? ''} (${session.me.tenant?.code ?? ''})`;
  const screenTitle = (!operator && SELLER_TITLES[titleKey]) || (TITLES[titleKey] ?? 'Pronto Depo');
  // the same entries for a phone's tab bar and its "Menü" sheet, in the rail's blocks
  const groups: NavGroup[] = (
    operator
      ? [
          { title: 'Depo işleri', items: FLOOR },
          { title: 'Kayıtlar', items: RECORDS },
          ...(session.isSupervisor ? [{ title: 'Yönetim', items: ADMIN }] : []),
        ]
      : [{ title: '', items: SELLER }]
  ).map((g) => ({ ...g, items: visibleTo(g.items, session.me.role) }));
  // the browser tab says it too: "Mal kabul · Depo" next to "Stok · YILMAZER"
  useEffect(() => {
    document.title = `${screenTitle} · ${operator ? 'Depo' : (session.me.tenant?.code ?? 'Müşteri')} · Pronto Depo`;
  }, [screenTitle, operator, session.me.tenant?.code]);

  const shell = (
    <div className="shell">
      <aside className="side">
        <div className="brand">
          <Logo />
          {/* which panel this is, and whose: one person may be signed in to both, in two tabs */}
          <div className={`panel-id ${operator ? 'depot' : 'seller'}`} title={panelTitle}>
            <span className="panel-kind">
              <span className="long">{operator ? 'Depo yönetimi' : 'Müşteri paneli'}</span>
              <span className="short">{operator ? 'DEPO' : 'MÜŞTERİ'}</span>
            </span>
            {operator ? (
              <span className="panel-who">{warehouse?.name ?? 'Depo'}</span>
            ) : (
              <>
                <span className="panel-who">{session.me.tenant?.name}</span>
                <span className="panel-code">{session.me.tenant?.code}</span>
              </>
            )}
          </div>
        </div>
        {operator ? (
          <>
            {nav(FLOOR)}
            <div className="nav-sep" />
            {nav(RECORDS)}
            {session.isSupervisor && (
              <>
                <div className="nav-sep" />
                {nav(ADMIN)}
              </>
            )}
          </>
        ) : (
          nav(SELLER)
        )}
        <div className="side-foot">
          <div className="user-chip" title={`${session.me.fullName} · ${roleLabel}`}>
            <span className="avatar" aria-hidden>
              {initials(session.me.fullName)}
            </span>
            <span className="who-text">
              <b>{session.me.fullName}</b>
              <small>{roleLabel}</small>
            </span>
          </div>
          <div className="foot-actions">
            <a href="#/ayarlar" title="Görünüm (gece / gündüz), şifre ve iki adımlı doğrulama">
              <Icon name="sliders" size={16} />
              <span className="label">Ayarlar</span>
              <span className="short" aria-hidden>
                Ayarlar
              </span>
            </a>
            <button onClick={session.logout} title="Çıkış yap">
              <Icon name="logout" size={16} />
              <span className="label">Çıkış</span>
              <span className="short" aria-hidden>
                Çıkış
              </span>
            </button>
          </div>
          {info?.version && (
            <div className="role" title="Yüklü sürüm (güncelleme ve geri dönüşte kontrol için)">
              Sürüm {info.version}
            </div>
          )}
          <div className="role brand-line">Pronto Depo, bir Yılmazer Lojistik hizmetidir</div>
        </div>
      </aside>
      <div className="main">
        {/* the acil iş bar stays under the top bar as the page scrolls */}
        <div className="topbar-wrap">
          <header className="topbar">
            <h1>
              <span className="long">{screenTitle}</span>
              <span className="short">{SHORT_TITLES[titleKey] ?? screenTitle}</span>
            </h1>
            {ribbon && (
              <span className={`chip env-chip ${ribbon.tone}`} title={info?.version ? `${ribbon.text} · sürüm ${info.version}` : ribbon.text}>
                <span className="long">{ribbon.text}</span>
                <span className="short">{ribbon.short}</span>
              </span>
            )}
            <span className="spacer" />
            {operator && session.warehouses.length > 1 && (
              <select className="wh-select" value={session.warehouseId ?? ''} onChange={(e) => session.setWarehouseId(e.target.value)} aria-label="Depo">
                {session.warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    WMS · {w.code}
                  </option>
                ))}
              </select>
            )}
            {operator && session.warehouses.length === 1 && <span className="wh-tag">WMS · {warehouse?.code}</span>}
            <HelpButton key={section} section={section} page={route.parts[1]} />
            <Bell active={section === 'bildirimler'} />
          </header>
          {/* a carrier changed, an order cancelled after the floor started on it: on every warehouse screen */}
          {operator && <UrgentBar section={section} />}
        </div>
        <main className="content">
          {newVersion && (
            <div className="banner info update-bar" role="status">
              <Icon name="refresh" />
              <div style={{ flex: 1 }}>Yeni sürüm yüklendi. Yeni özellikleri görmek için sayfayı yenileyin.</div>
              <button className="btn sm primary" onClick={() => window.location.reload()}>
                Yenile
              </button>
            </div>
          )}
          {/* the desk's printer computer prints whatever screen is open; packing and picking warn when it is gone */}
          {operator && <DeskPrinter section={section} />}
          <StaleScope>
            <ScreenBoundary key={route.path}>{view(route, operator, () => void summary.reload())}</ScreenBoundary>
          </StaleScope>
        </main>
      </div>
      {/* a phone: the rail gives way to tabs along the bottom */}
      <TabBar
        groups={groups}
        preferred={operator ? FLOOR_TABS : SELLER_TABS}
        path={route.path}
        summary={summary.data}
        roleLabel={roleLabel}
        place={operator ? (warehouse?.name ?? 'Depo') : `${session.me.tenant?.name ?? ''} (${session.me.tenant?.code ?? ''})`}
        avatar={initials(session.me.fullName)}
        version={info?.version}
      />
    </div>
  );
  // the warehouse's screens share one look at the open acil işler (the bar, its panel, Özet)
  return operator ? <UrgentProvider>{shell}</UrgentProvider> : shell;
}

/** The bell: unread bildirimler, counted every 30 seconds and again whenever they are read or deleted. */
function Bell({ active }: { active: boolean }) {
  const count = useLoad(() => api<{ unread: number }>('GET', '/api/notifications/count'), [], 30_000);
  useEffect(() => {
    const again = () => void count.reload();
    window.addEventListener(NOTIFICATIONS_CHANGED, again);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED, again);
  }, []);
  const unread = count.data?.unread ?? 0;
  return (
    <a className={`icon-btn bell${active ? ' on' : ''}`} href="#/bildirimler" title="Bildirimler" aria-label={unread > 0 ? `Bildirimler, ${unread} okunmamış` : 'Bildirimler'}>
      <Icon name="bell" />
      {unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
    </a>
  );
}

/** "Selin Kaya" → "SK", for the avatar in the menu. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toLocaleUpperCase('tr');
}

/** A broken screen shows a message instead of a blank page; switching screens resets it. */
class ScreenBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('screen failed', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="banner bad" role="alert">
        <div style={{ flex: 1 }}>
          Bu ekran açılırken bir hata oluştu.
          <div style={{ fontWeight: 500 }}>{this.state.error.message}</div>
        </div>
        <button className="btn sm" onClick={() => window.location.reload()}>
          Yenile
        </button>
      </div>
    );
  }
}

function view(route: Route, operator: boolean, refreshSummary: () => void): ReactNode {
  const [section, id, mode] = route.parts;
  if (section === 'sifre') return <ChangePassword />;
  if (section === 'guvenlik' || section === 'ayarlar') return <Settings />;
  if (section === 'bildirimler') return <Notifications />;
  if (section === 'performans') return <Performance />;
  if (section === 'urunler') {
    if (id === 'yeni') return <ProductForm id={null} />;
    if (id === 'ice-aktar') return <ImportProducts />;
    if (id === 'eslestirme') return <Mapping />;
    return id ? <ProductForm id={id} /> : operator ? <Products /> : <SellerProducts />;
  }
  if (!operator) {
    switch (section) {
      case 'kullanicilar':
        return <SellerUsers />;
      case 'siparisler':
        if (id === 'yeni') return <NewOrder />;
        if (id === 'ice-aktar') return <OrderImport />;
        return <Orders />;
      case 'iadeler':
        return <SellerReturns />;
      case 'asn':
        return <SellerAsn id={id ?? null} mode={mode ?? null} />;
      case 'stok':
        // on its way to Ürünler ve stok (Shell)
        return null;
      case 'e-belge':
        return <SellerDocuments onChange={refreshSummary} />;
      case 'pazaryerleri':
        return <Marketplaces onChange={refreshSummary} />;
      case 'faturalar':
        return <SellerBilling invoiceId={id ?? null} />;
      case 'karlilik':
        return <SellerProfit />;
      default:
        return <SellerHome />;
    }
  }
  switch (section) {
    case undefined:
      return <Dashboard />;
    case 'kabul':
      return <Receiving asn={id ?? null} />;
    case 'iade':
      return <ReturnsDesk onChange={refreshSummary} />;
    case 'yerlestirme':
      return <Putaway />;
    case 'toplama':
      return id ? <PickWalk routeId={id} /> : <PickRoutes />;
    case 'paketleme':
      return <Packing orderId={id ?? null} onChange={refreshSummary} />;
    case 'sevkiyat':
      return <Dispatch />;
    case 'sorunlu':
      return <Exceptions onChange={refreshSummary} />;
    case 'stok-bekleyen':
      return <StockWaiting onChange={refreshSummary} />;
    case 'sayim':
      return <Counts onChange={refreshSummary} />;
    case 'acil':
      return <UrgentPanel />;
    case 'siparisler':
      return <Orders />;
    case 'stok':
      return <Stock />;
    case 'harita':
      return <FloorMap />;
    case 'yonetim':
      if (id === 'kullanicilar') return <AdminUsers />;
      if (id === 'depo') return <WarehouseBuilder />;
      if (id === 'kargo') return <Carriers />;
      if (id === 'fiyatlar') return <ListPrice />;
      if (id === 'sistem') return <Operations onChange={refreshSummary} />;
      if (id === 'musteriler') return mode === 'yeni' ? <NewTenant /> : mode ? <TenantPage id={mode} /> : <Tenants />;
      return <Tenants />;
    default:
      return <Dashboard />;
  }
}
